import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { issueFirstUse } from '@/lib/onboarding/firstUseFlag';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SignatureCanvas } from '@/components/legal/SignatureCanvas';
import { BETA_AGREEMENT_CONTENT, BETA_AGREEMENT_TITLE } from '@/legal/beta_agreement';
import { PRIVACY_CONTENT, PRIVACY_TITLE } from '@/legal/privacy';
import { SHOP_TOS_CONTENT, SHOP_TOS_TITLE } from '@/legal/tos_shop';
import {
  CURRENT_BETA_AGREEMENT_VERSION,
  CURRENT_PRIVACY_VERSION,
  CURRENT_SHOP_TOS_VERSION,
  CURRENT_AGE_VERIFICATION_VERSION,
} from '@/legal/versions';
import { describeRosterAdoption, parseRosterEntry, type RosterEntryAdoption } from '@/lib/onboarding/rosterAdoption';
import { Loader2, Building2, FileSignature, ShieldCheck, CheckCircle2, Circle } from 'lucide-react';

type Step = 'loading' | 'sign_agreement' | 'legal_review' | 'confirm_adoption' | 'accepting' | 'done' | 'error';

const SCROLL_THRESHOLD = 40;

const JoinTeamAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();

  const [step, setStep] = useState<Step>('loading');
  const [shopName, setShopName] = useState('');
  const [shopId, setShopId] = useState('');
  const [role, setRole] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Beta signing state
  const [signerName, setSignerName] = useState('');
  const [position, setPosition] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [signatureMethod, setSignatureMethod] = useState<'draw' | 'typed'>('draw');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [esignConsent, setEsignConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Legal review state
  const [privacyScrolled, setPrivacyScrolled] = useState(false);
  const [tosScrolled, setTosScrolled] = useState(false);
  const [ageChecked, setAgeChecked] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);

  const ranOnce = useRef(false);

  // WS-ELIG B-E4: the roster entry this invitation names (if any). Its adoption
  // is disclosed on a confirmation step before accepting; never auto-accepted.
  const [rosterEntry, setRosterEntry] = useState<RosterEntryAdoption | null>(null);
  const rosterEntryRef = useRef<RosterEntryAdoption | null>(null);

  // Redirect to JoinTeam if not authenticated
  useEffect(() => {
    if (!user && !token) {
      navigate('/auth', { replace: true });
    } else if (!user && token) {
      navigate(`/join-team?token=${token}`, { replace: true });
    }
  }, [user, token, navigate]);

  // Determine which legal step we need
  useEffect(() => {
    if (!user || !token || ranOnce.current) return;
    ranOnce.current = true;

    const checkLegalStatus = async () => {
      try {
        // Fetch invitation details for context
        const { data: invData, error: invErr } = await supabase.functions.invoke('get-team-invitation-details', {
          body: { token },
        });

        if (invErr || !invData?.valid) {
          setStep('error');
          setErrorMessage(invData?.error === 'expired' ? 'This invitation has expired.' : 'Invalid invitation.');
          return;
        }

        setShopName(invData.shop_name);
        setShopId(invData.shop_id);
        setRole(invData.role);
        const entry = parseRosterEntry(invData.roster_entry);
        rosterEntryRef.current = entry;
        setRosterEntry(entry);
        setSignerName(user.user_metadata?.display_name || '');
        setEmail(user.email || '');

        // Check if beta agreement is already signed
        const { data: betaSigned } = await supabase
          .from('legal_acceptances')
          .select('id')
          .eq('user_id', user.id)
          .eq('agreement_type', 'beta_agreement_signed')
          .eq('agreement_version', CURRENT_BETA_AGREEMENT_VERSION)
          .maybeSingle();

        if (!betaSigned) {
          setStep('sign_agreement');
          return;
        }

        // Check privacy, TOS, age
        const checks = await Promise.all([
          supabase.from('legal_acceptances').select('id')
            .eq('user_id', user.id).eq('agreement_type', 'privacy_policy')
            .eq('agreement_version', CURRENT_PRIVACY_VERSION).maybeSingle(),
          supabase.from('legal_acceptances').select('id')
            .eq('user_id', user.id).eq('agreement_type', 'tos_shop')
            .eq('agreement_version', CURRENT_SHOP_TOS_VERSION).maybeSingle(),
          supabase.from('legal_acceptances').select('id')
            .eq('user_id', user.id).eq('agreement_type', 'age_verification')
            .eq('agreement_version', CURRENT_AGE_VERIFICATION_VERSION).maybeSingle(),
        ]);

        if (checks.some(c => !c.data)) {
          setStep('legal_review');
          return;
        }

        // All legal done — proceed to accept
        await proceedToAccept();
      } catch (err: any) {
        console.error('JoinTeamAccept init error:', err);
        setStep('error');
        setErrorMessage('Something went wrong. Please try again.');
      }
    };

    checkLegalStatus();
  }, [user, token]);

  // WS-ELIG B-E4: an invitation that names a roster entry stops on a
  // confirmation step that discloses the adoption; the person's click is the
  // authority. Without a named entry nothing changes (straight to accept).
  const proceedToAccept = async () => {
    if (rosterEntryRef.current) {
      setStep('confirm_adoption');
      return;
    }
    await acceptInvitation(null);
  };

  const acceptInvitation = async (adoptRosterEntryId: string | null) => {
    if (!token || !user) return;
    setStep('accepting');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const { data, error } = await supabase.functions.invoke('accept-team-invitation', {
        body: adoptRosterEntryId ? { token, adopt_roster_entry: adoptRosterEntryId } : { token },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      if (error || !data?.success) {
        let code = (data as any)?.error || '';
        if (!code && error) {
          try {
            const ctx = (error as any)?.context;
            if (ctx instanceof Response) {
              const body = await ctx.clone().json();
              if (body?.error) code = body.error;
            }
          } catch { /* best-effort; ignore */ }
        }

        setStep('error');
        if (code === 'email_mismatch') {
          setErrorMessage(`You're signed in as ${user.email}, but this invite is for a different email. Please sign out and try again.`);
        } else if (code === 'expired') {
          setErrorMessage('This invitation has expired.');
        } else if (code === 'already_used') {
          setErrorMessage('This invitation has already been used.');
        } else {
          setErrorMessage('Failed to accept invitation. Please try again or contact the team manager.');
        }
        return;
      }

      // FIX-R32-ITER2-2: defensive guard — don't write an empty/undefined
      // shop id into activeShopId; a null string would later cause
      // unscoped `.eq('shop_id', null)` queries on page reload.
      const targetShopId = data.shop_id || shopId;
      if (targetShopId) {
        localStorage.setItem('activeShopId', targetShopId);
        // CORE-BL: issue the one automatic first-use invitation, for THIS
        // shop, now that the person is actually a member of it.
        //
        // This is the join path an invited technician, advisor, dispatcher or
        // parts person actually takes, and it never issued one. Delivery was
        // built, made global and tested — and the only real journey that
        // should have triggered it set `activeShopId`, reloaded, and moved
        // on. Every test that exercised first use had manufactured the
        // invitation itself, so nothing noticed that no user journey created
        // one.
        //
        // Issued only on a SUCCESSFUL acceptance (the failure branches above
        // all returned), and named with the target shop so landing with a
        // different shop active cannot spend it on the wrong walkthrough.
        issueFirstUse(targetShopId);
      } else {
        console.warn('[JoinTeamAccept] acceptInvitation returned no shop_id; leaving activeShopId unchanged');
      }
      // Clear any leftover joinTeamToken
      localStorage.removeItem('joinTeamToken');

      setStep('done');
      toast({ title: 'Welcome aboard!', description: `You've joined ${shopName || 'the team'}.` });

      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err: any) {
      console.error('Accept invitation error:', err);
      setStep('error');
      setErrorMessage('Failed to accept invitation.');
    }
  };

  const handleSignAgreement = async () => {
    if (!user || !shopId) return;
    const hasValidSig = signatureMethod === 'draw' ? !!signatureImage : typedSignature.trim().length > 0;
    if (!signerName || !position || !email || !hasValidSig || !esignConsent) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('sign-beta-agreement', {
        body: {
          shopId,
          shopName,
          signerName,
          position,
          address,
          email,
          mobile,
          signatureImage: signatureMethod === 'draw' ? signatureImage : null,
          typedSignature: signatureMethod === 'typed' ? typedSignature : null,
          signatureMethod,
        },
      });

      if (error) throw error;

      toast({ title: 'Agreement Signed', description: 'Beta agreement signed successfully.' });
      setStep('legal_review');
    } catch (err: any) {
      console.error('Sign agreement error:', err);
      toast({ title: 'Signing Failed', description: err.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLegalSubmit = async () => {
    if (!user) return;
    setIsSubmitting(true);

    try {
      const ua = navigator.userAgent || 'unknown';
      const acceptances = [
        { user_id: user.id, agreement_type: 'privacy_policy', agreement_version: CURRENT_PRIVACY_VERSION, user_agent: ua },
        { user_id: user.id, agreement_type: 'tos_shop', agreement_version: CURRENT_SHOP_TOS_VERSION, user_agent: ua },
        { user_id: user.id, agreement_type: 'age_verification', agreement_version: CURRENT_AGE_VERIFICATION_VERSION, user_agent: ua },
      ];

      const { error } = await supabase
        .from('legal_acceptances')
        .upsert(acceptances, { onConflict: 'user_id,agreement_type,agreement_version', ignoreDuplicates: true });

      if (error) throw error;

      toast({ title: 'Legal Review Complete', description: 'Privacy Policy and Terms accepted.' });
      await proceedToAccept();
    } catch (err: any) {
      console.error('Legal review error:', err);
      toast({ title: 'Submission Failed', description: err.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScroll = useCallback(
    (setter: (v: boolean) => void) => (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD) setter(true);
    },
    []
  );

  const formatRole = (r: string) => r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Loading
  if (step === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Setting up your team access...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Accepting / Done
  if (step === 'accepting' || step === 'done') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {step === 'accepting' ? (
              <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin mb-2" />
            ) : (
              <CheckCircle2 className="h-10 w-10 mx-auto text-green-600 mb-2" />
            )}
            <CardTitle>{step === 'accepting' ? 'Joining Team...' : 'Welcome Aboard!'}</CardTitle>
            <CardDescription>
              {step === 'accepting'
                ? `Setting up your access to ${shopName}...`
                : `You've joined ${shopName} as ${formatRole(role)}. Redirecting...`}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // WS-ELIG B-E4: disclose the roster-entry adoption before accepting.
  if (step === 'confirm_adoption' && rosterEntry) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="roster-adoption-confirm">
          <CardHeader className="text-center">
            <Building2 className="h-10 w-10 mx-auto text-primary mb-2" />
            <CardTitle>Join {shopName}</CardTitle>
            <CardDescription>You are already on this shop&apos;s roster.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {describeRosterAdoption(rosterEntry, shopName).map((line) => (
              <p key={line} className="text-sm text-muted-foreground">{line}</p>
            ))}
            <Button className="w-full" data-testid="roster-adoption-accept"
              onClick={() => { void acceptInvitation(rosterEntry.technicianId); }}>
              Join and take over this entry
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
              Not now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error
  if (step === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Building2 className="h-10 w-10 mx-auto text-destructive mb-2" />
            <CardTitle>Unable to Join</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">Return to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation context banner
  const inviteBanner = (
    <div className="rounded-lg border border-border bg-muted/50 p-4 mb-6 text-center">
      <p className="text-sm text-muted-foreground">
        Joining <strong>{shopName}</strong> as <strong>{formatRole(role)}</strong>
      </p>
    </div>
  );

  // Beta Agreement Signing
  if (step === 'sign_agreement') {
    const hasValidSig = signatureMethod === 'draw' ? !!signatureImage : typedSignature.trim().length > 0;
    const canSubmit = signerName && position && email && hasValidSig && esignConsent && !isSubmitting;

    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {inviteBanner}

          <div className="text-center space-y-2">
            <FileSignature className="h-10 w-10 mx-auto text-primary" />
            <h1 className="text-2xl font-bold">{BETA_AGREEMENT_TITLE}</h1>
            <p className="text-muted-foreground">
              Please review and electronically sign the beta testing agreement below.
            </p>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Agreement Text</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64 overflow-y-auto rounded-md border p-4">
                <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
                  {BETA_AGREEMENT_CONTENT}
                </pre>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Signer Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shopName">Shop Name</Label>
                  <Input id="shopName" value={shopName} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signerName">Your Name *</Label>
                  <Input id="signerName" value={signerName} onChange={e => setSignerName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position / Title *</Label>
                  <Input id="position" value={position} onChange={e => setPosition(e.target.value)} placeholder="e.g. Technician, Advisor" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={address} onChange={e => setAddress(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input id="mobile" type="tel" value={mobile} onChange={e => setMobile(e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Signature</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={signatureMethod} onValueChange={v => setSignatureMethod(v as 'draw' | 'typed')}>
                <TabsList>
                  <TabsTrigger value="draw">Draw Signature</TabsTrigger>
                  <TabsTrigger value="typed">Type Signature</TabsTrigger>
                </TabsList>
                <TabsContent value="draw" className="mt-4">
                  <SignatureCanvas onSignatureChange={setSignatureImage} />
                </TabsContent>
                <TabsContent value="typed" className="mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="typedSig">Type your full legal name</Label>
                    <Input
                      id="typedSig"
                      value={typedSignature}
                      onChange={e => setTypedSignature(e.target.value)}
                      className="text-xl italic font-serif"
                      placeholder="Full legal name"
                    />
                    {typedSignature && (
                      <p className="text-2xl italic font-serif text-primary mt-2 border-b border-primary/30 pb-1 inline-block">
                        {typedSignature}
                      </p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex items-start gap-2 pt-4">
                <Checkbox id="esignConsent" checked={esignConsent} onCheckedChange={v => setEsignConsent(v === true)} />
                <Label htmlFor="esignConsent" className="text-sm leading-relaxed cursor-pointer">
                  I agree to electronically sign this agreement and receive a copy electronically.
                </Label>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSignAgreement} disabled={!canSubmit}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign Agreement & Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Legal Review (Privacy + TOS + Age)
  if (step === 'legal_review') {
    const canSubmit = privacyScrolled && tosScrolled && ageChecked && agreeChecked && !isSubmitting;

    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {inviteBanner}

          <div className="text-center space-y-2">
            <ShieldCheck className="h-10 w-10 mx-auto text-primary" />
            <h1 className="text-2xl font-bold">Review Legal Agreements</h1>
            <p className="text-muted-foreground">Please read each document in full before accepting.</p>
          </div>

          <div className="flex items-center justify-center gap-6 text-sm">
            <span className="flex items-center gap-1.5">
              {privacyScrolled ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
              Privacy Policy
            </span>
            <span className="flex items-center gap-1.5">
              {tosScrolled ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
              Terms of Service
            </span>
          </div>

          <Card>
            <CardContent className="pt-6">
              <Tabs defaultValue="privacy">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="privacy" className="flex items-center gap-1.5">
                    {privacyScrolled && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                    {PRIVACY_TITLE}
                  </TabsTrigger>
                  <TabsTrigger value="tos" className="flex items-center gap-1.5">
                    {tosScrolled && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                    {SHOP_TOS_TITLE}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="privacy" className="mt-4">
                  <div className="h-80 overflow-y-auto rounded-md border p-4" onScroll={handleScroll(setPrivacyScrolled)}>
                    <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">{PRIVACY_CONTENT}</pre>
                  </div>
                  {!privacyScrolled && <p className="text-xs text-muted-foreground mt-2 text-center">Scroll to the bottom to continue</p>}
                </TabsContent>

                <TabsContent value="tos" className="mt-4">
                  <div className="h-80 overflow-y-auto rounded-md border p-4" onScroll={handleScroll(setTosScrolled)}>
                    <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">{SHOP_TOS_CONTENT}</pre>
                  </div>
                  {!tosScrolled && <p className="text-xs text-muted-foreground mt-2 text-center">Scroll to the bottom to continue</p>}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Acceptance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2">
                <Checkbox id="age-verify" checked={ageChecked} onCheckedChange={v => setAgeChecked(v === true)} />
                <Label htmlFor="age-verify" className="text-sm leading-relaxed cursor-pointer">
                  I confirm I am at least 18 years of age
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="agree-legal"
                  checked={agreeChecked}
                  onCheckedChange={v => setAgreeChecked(v === true)}
                  disabled={!privacyScrolled || !tosScrolled}
                />
                <Label htmlFor="agree-legal" className={`text-sm leading-relaxed cursor-pointer ${!privacyScrolled || !tosScrolled ? 'text-muted-foreground' : ''}`}>
                  I have read and agree to the Privacy Policy and Terms of Service
                </Label>
              </div>
              {(!privacyScrolled || !tosScrolled) && (
                <p className="text-xs text-muted-foreground">You must scroll to the bottom of both documents before you can accept.</p>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleLegalSubmit} disabled={!canSubmit}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accept & Join Team
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default JoinTeamAccept;
